import { Trans } from 'react-i18next';

import {
   HealthcareProviderAppointmentConfirmationPractitionerNotificationConfigurationQuery,
   HealthcareProviderAppointmentConfirmationPractitionerNotificationEnabledSetCommand,
} from '@dreryk/egabinet-phone-notification-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { useFormController } from '@dreryk/react-common';
import {
   dispatchSnackbarPositiveEvent,
   IconFillColor,
   IconSize,
   IconWrapper,
   QuestionmarkCircleIcon,
   StyledFlex,
   Toggle,
   Tooltip,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

import { PhoneNotificationTooltipText } from './PhoneNotificationTooltipText';
import { PractitionerPhoneNotificationAppointmentConfirmationToggleLoader } from './PractitionerPhoneNotificationAppointmentConfirmationToggle.loader';

const SAVED_SNACKBAR_DELAY_IN_MS = 5000;
const useCurrentHealthcareProviderAppointmentConfirmationPractitionerNotificationEnabledSetCommand = (): UseCqrsCommandResult<
   void,
   PractitionerPhoneNotificationAppointmentConfirmationToggleFormBean
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new HealthcareProviderAppointmentConfirmationPractitionerNotificationEnabledSetCommand();
         command.enabled = Choice.of(parameters.notificationEnabled ?? false);
         return command;
      },
      {
         onSuccess: (ignore, parameters) => {
            dispatchSnackbarPositiveEvent(
               parameters.notificationEnabled
                  ? patientPhoneNotificationTranslationKeys.notificationToggle
                       .appointmentPractitionerConfirmationToggleSaveOn
                  : patientPhoneNotificationTranslationKeys.notificationToggle
                       .appointmentPractitionerConfirmationToggleSaveOff,
               {
                  autoHideDelayInMs: SAVED_SNACKBAR_DELAY_IN_MS,
               }
            );
            refetchCqrsQuery(HealthcareProviderAppointmentConfirmationPractitionerNotificationConfigurationQuery);
         },
      }
   );
};

type PractitionerPhoneNotificationAppointmentConfirmationToggleFormBean = {
   notificationEnabled: boolean;
};

export const PractitionerPhoneNotificationAppointmentConfirmationToggle = (props: {
   readonly notificationEnabled: boolean;
   readonly messages: string[];
}): JSX.Element | null => {
   const {
      register,
      handleSubmit,
   } = useFormController<PractitionerPhoneNotificationAppointmentConfirmationToggleFormBean>(
      {
         notificationEnabled: props.notificationEnabled,
      },
      { mode: 'onChange' }
   );
   const {
      executeAsync: enablePractitionerNotificationSet,
      loading,
   } = useCurrentHealthcareProviderAppointmentConfirmationPractitionerNotificationEnabledSetCommand();

   return (
      <StyledFlex gap={'12px'}>
         <form onInput={handleSubmit(enablePractitionerNotificationSet)}>
            <Toggle
               controller={register('notificationEnabled')}
               testId={'practitionerPhoneNotificationAppointmentConfirmationToggle'}
               disabled={loading}
            />
         </form>
         <StyledFlex gap={'4px'} alignItems={'center'}>
            <Trans
               i18nKey={
                  patientPhoneNotificationTranslationKeys.notificationToggle.appointmentPractitionerConfirmationToggle
               }
            />
            <Tooltip
               maxWidth={'600px'}
               testId={'appointmentPractitionerConfirmationToggleTooltip'}
               content={
                  <PhoneNotificationTooltipText
                     header={
                        patientPhoneNotificationTranslationKeys.notificationToggle
                           .appointmentPractitionerConfirmationToggleTooltip
                     }
                     messages={props.messages}
                  />
               }
            >
               <IconWrapper
                  testId={'appointmentPractitionerConfirmationToggleTooltipIconWrapper'}
                  fillColor={IconFillColor.PRIMARY}
                  size={IconSize.SMALL}
               >
                  <QuestionmarkCircleIcon />
               </IconWrapper>
            </Tooltip>
         </StyledFlex>
      </StyledFlex>
   );
};

export const PractitionerPhoneNotificationAppointmentConfirmationToggleWithLoader = (): JSX.Element => {
   return (
      <PractitionerPhoneNotificationAppointmentConfirmationToggleLoader>
         {({ notificationEnabled, messages }) => (
            <PractitionerPhoneNotificationAppointmentConfirmationToggle
               notificationEnabled={notificationEnabled}
               messages={messages}
            />
         )}
      </PractitionerPhoneNotificationAppointmentConfirmationToggleLoader>
   );
};
